import axios from 'axios';
import { mockCategories } from './mockData';

export interface StoreConfig {
  primaryColor: string;
  logo: string;
  name: string;
}

export interface Brand {
  id: number;
  name: string;
  image: string | null;
  code: string;
  is_active: boolean;
  company: number;
}

export interface ProductImage {
  id: number;
  name: string;
  image: string;
  is_banner: boolean;
  type: string;
  product: number;
  created_at?: string;
  updated_at?: string;
}

export interface Product {
  id: number;
  name: string;
  code: string;
  description: string | null;
  mrp: number;
  retailer_rate: number;
  selling_price?: number;
  brand: Brand;
  images: ProductImage[];
  is_active: boolean;
  base_quantity: number | null;
  base_unit: string | null;
  sub_category: number;
  is_quick_product: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubCategory {
  id: number;
  name: string;
}

export interface Category {
  id: number;
  name: string;
  description: string | null;
  image: string | null;
  parent: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Storefront {
  id: number;
  created_at: string;
  updated_at: string;
  domain: string;
  primary_color: string;
  company: number;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.riggleapp.in',
  headers: {
    'Content-Type': 'application/json',
    'x-app-name': 'retailer'
  }
});

// Using mock data instead of API calls for now
export const getCategories = async (): Promise<Category[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  return mockCategories;
};

export const getProducts = async (categoryId?: string, searchQuery?: string): Promise<Product[]> => {
  try {
    const params: Record<string, string> = {
      domain: window.location.hostname,
    };

    if (categoryId) {
      params.sub_category_id = categoryId;
    }

    if (searchQuery && searchQuery.length >= 3) {
      params.search = searchQuery;
    }

    const response = await api.get('/api/v1/storefront/storefronts/list-products/', {
      params
    });
    // Adapt to new response format: product info is nested under 'product' key
    let products = response.data.map((item: any) => {
      const prod = item.product || {};
      return {
        id: prod.id,
        name: prod.name,
        code: prod.code,
        description: prod.description,
        mrp: prod.mrp,
        retailer_rate: item.retailer_rate ?? item.selling_price ?? 0,
        selling_price: item.selling_price ?? 0,
        brand: prod.brand ?? { id: 0, name: '', image: null, code: '', is_active: true, company: 0 },
        images: item.images ?? [],
        is_active: prod.is_active,
        base_quantity: prod.base_quantity ?? null,
        base_unit: prod.base_unit ?? null,
        sub_category: prod.sub_category ?? 0,
        is_quick_product: prod.is_quick_product ?? false,
        created_at: prod.created_at,
        updated_at: prod.updated_at,
      };
    });
    // Sort: 1. With images first, 2. Then by brand name (same brand together)
    products.sort((a: Product, b: Product) => {
      const aHasImage = a.images && a.images.length > 0;
      const bHasImage = b.images && b.images.length > 0;
      if (aHasImage !== bHasImage) return aHasImage ? -1 : 1;
      // If both have (or don't have) images, sort by brand name
      const brandA = a.brand?.name?.toLowerCase?.() || '';
      const brandB = b.brand?.name?.toLowerCase?.() || '';
      if (brandA < brandB) return -1;
      if (brandA > brandB) return 1;
      return 0;
    });
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const getSubCategories = async () => {
  try {
    // For now using mock data, replace with actual API call
    const response = await api.get('/api/v1/storefront/storefronts/list-sub-categories/', {
      params: { domain: window.location.hostname }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching sub-categories:', error);
    throw error;
  }
};

export const getStorefront = async (): Promise<Storefront> => {
  try {
    const response = await api.get('/api/v1/storefront/storefronts/get-by-domain/', {
      params: { domain: window.location.hostname }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching storefront:', error);
    throw error;
  }
};

export const getStoreConfig = async (): Promise<StoreConfig> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  return {
    primaryColor: '#1976d2',
    logo: 'https://source.unsplash.com/random/200x80/?logo',
    name: 'Sample Store'
  };
};

export const fetchCategories = async (): Promise<Category[]> => {
  const response = await api.get<Category[]>('/api/v1/storefront/storefronts/list-sub-categories/', {
    params: { domain: window.location.hostname }
  });
  return response.data;
};

export const fetchProducts = async (): Promise<Product[]> => {
  const response = await api.get<Product[]>('/api/v1/store/products/');
  return response.data;
};

export const fetchProductsByCategory = async (categoryId: number): Promise<Product[]> => {
  const response = await api.get<Product[]>(`/api/v1/storefront/storefronts/sub-categories/${categoryId}/products/`, {
    params: { domain: window.location.hostname }
  });
  return response.data;
};
